import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, usePropertyAddressUrl, usePropertyField, useProperty } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';

// ============================================================================
// Navigation Structure (Crosby override: adds Deals folder with CORT + Dallas)
// ============================================================================

interface NavChild {
  label: string;
  href: string;
}

interface NavItem {
  label: string;
  href?: string;
  children?: NavChild[];
}

/**
 * The "Find Your Home" grouping only earns a dropdown when the community has an
 * interactive map to pair with Floor Plans; otherwise the nav shows a single
 * top-level Floor Plans link instead (see FLOOR_PLANS_NAV_ITEM).
 */
const FIND_YOUR_HOME_NAV_ITEM: NavItem = {
  label: 'Find Your Home',
  children: [
    { label: 'Floor Plans', href: '/floor-plans' },
    { label: 'Interactive Map', href: '/interactive-map' },
  ],
};

const FLOOR_PLANS_NAV_ITEM: NavItem = { label: 'Floor Plans', href: '/floor-plans' };

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Features',
    children: [
      { label: 'Amenities', href: '/amenities' },
      { label: 'Pet Policy', href: '/pet-policy' },
      { label: 'Future Residents Club', href: '/future-residents-club' },
    ],
  },
  {
    label: 'Gallery & Tours',
    children: [
      { label: 'Gallery', href: '/gallery' },
      { label: 'Virtual Tours', href: '/virtual-tour' },
    ],
  },
  { label: 'Neighborhood', href: '/neighborhood' },
  { label: 'Contact Us', href: '/contact-us' },
  {
    label: 'Deals',
    children: [
      { label: 'CORT Furniture Rental', href: '/CORT-furniture-rental' },
      { label: 'Dallas Mavericks Games', href: '/dallas-mavericks-games' },
    ],
  },
];

/** Home link — included in mobile hamburger menu only */
const HOME_NAV_ITEM: NavItem = { label: 'Home', href: '/' };

/**
 * Which communities have an interactive map. Engrain SightMap is the platform's
 * interactive-map integration (see the `engrain-sitemap` block), so the community's
 * SightMap custom field is the signal. The CMS writes it under either spelling and
 * as either an id or a full URL, so all four variants count.
 */
const INTERACTIVE_MAP_FIELDS = [
  'custom_engrain_sightmap_id',
  'custom_engrain_sightmap_url',
  'custom_engrain_sitemap_id',
  'custom_engrain_sitemap_url',
];

function useHasInteractiveMap(): boolean {
  const property = useProperty();

  return INTERACTIVE_MAP_FIELDS.some((field) => {
    const value = property[field];
    // The CMS stores an unset custom field as the literal string 'false'
    return typeof value === 'string' && value.trim() !== '' && value !== 'false';
  });
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const qualifyingCriteriaLinkDef: SlotGroupDefinition = {
  label: 'Qualifying Criteria Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/qualifying-criteria' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Qualifying Criteria' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// SVG Icon Components
// ============================================================================

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function toTelHref(raw: string | null | undefined): string {
  const cleaned = (raw ?? '').replace(/[^+\d]/g, '');
  return cleaned ? `tel:${cleaned}` : '#';
}

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

// ============================================================================
// Desktop Dropdown Component
// ============================================================================

function NavDropdown({
  item,
  isOpen,
  onOpen,
  onClose,
}: {
  item: NavItem;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  if (!item.children) {
    return (
      <a
        href={item.href}
        className="font-body text-[13px] font-semibold tracking-wider uppercase text-white/90 hover:text-white transition-colors py-2"
      >
        {item.label}
      </a>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
    >
      <button
        onClick={() => (isOpen ? onClose() : onOpen())}
        className="flex items-center gap-1.5 font-body text-[13px] font-semibold tracking-wider uppercase text-white/90 hover:text-white transition-colors py-2"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {item.label}
        <ChevronDownIcon
          className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      <div
        className={`absolute top-full left-0 pt-1 transition-all duration-200 ${
          isOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-1'
        }`}
      >
        <div className="min-w-[220px] bg-white rounded shadow-lg border border-[var(--vibe-color-border)] py-1 overflow-hidden">
          {item.children.map((child) => (
            <a
              key={child.href}
              href={child.href}
              className="block px-5 py-2.5 text-sm font-body text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-surface)] hover:pl-6 transition-all duration-150"
            >
              {child.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Mobile Accordion Nav Item
// ============================================================================

function MobileNavItem({
  item,
  isOpen,
  onToggle,
  onNavigate,
}: {
  item: NavItem;
  isOpen: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}) {
  if (!item.children) {
    return (
      <a
        href={item.href}
        onClick={onNavigate}
        className="block py-3.5 px-6 font-body text-sm font-semibold uppercase tracking-wider text-white/90 hover:text-white transition-colors border-b border-white/10"
      >
        {item.label}
      </a>
    );
  }

  return (
    <div className="border-b border-white/10">
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full py-3.5 px-6 font-body text-sm font-semibold uppercase tracking-wider text-white/90 hover:text-white transition-colors"
        aria-expanded={isOpen}
      >
        {item.label}
        <ChevronDownIcon
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-white/5 py-1">
          {item.children.map((child) => (
            <a
              key={child.href}
              href={child.href}
              onClick={onNavigate}
              className="block py-3 px-10 font-body text-sm text-white/50 hover:text-white/80 transition-colors"
            >
              {child.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [openMobileAccordion, setOpenMobileAccordion] = useState<number | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLElement>(null);

  const phoneOverride = useContentToken('header.phone', { defaultValue: '' });
  const phoneFallback = usePropertyField('property_phone') as string | null;
  const phone = phoneOverride || phoneFallback || '(555) 555-5555';
  const fullAddress = useContentToken('header.full_address');

  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const primaryLogo = usePropertyField('property_primary_logo');

  const logo = { ...logoRaw, image: monoWhiteLogo || primaryLogo || logoRaw.image };

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const qualifyingCriteriaLink = useSlotGroup('header.qualifying_criteria_link', qualifyingCriteriaLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);

  // Only communities with a map configured get the Interactive Map link
  const hasInteractiveMap = useHasInteractiveMap();

  const { desktopNavItems, mobileNavItems } = useMemo(() => {
    const base = [
      HOME_NAV_ITEM,
      hasInteractiveMap ? FIND_YOUR_HOME_NAV_ITEM : FLOOR_PLANS_NAV_ITEM,
      ...NAV_ITEMS,
    ];
    return { desktopNavItems: base, mobileNavItems: base };
  }, [hasInteractiveMap]);

  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    updateHeaderHeight();
    const observer = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    return () => observer.disconnect();
  }, [updateHeaderHeight]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Directions link resolves via usePropertyAddressUrl(), which internally
  // walks the canonical place URL → business URL → full address → lat/lon
  // fallback chain. Replaces the old direct read of the Google place URL.
  const propertyAddressUrl = usePropertyAddressUrl();
  const directionsUrl = propertyAddressUrl || '#';

  const closeMobileMenu = () => {
    setMenuOpen(false);
    setOpenMobileAccordion(null);
  };

  return (
    <>
      <header
        ref={headerRef}
        className="fixed left-0 right-0 z-50 w-full"
        style={{ top: 'var(--offer-banner-height, 0px)', transition: 'top 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
      >
        {/* ================================================================ */}
        {/* Utility Bar                                                      */}
        {/* ================================================================ */}
        <div className="header-utility-bar">
          <div className="px-4 sm:px-5">
            <div className="flex items-center justify-between h-9">
              {/* Left: Address + Phone (desktop) */}
              <div className="header-utility-bar__contact hidden md:flex items-center divide-x divide-white/20">
                <a
                  href={directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="header-utility-bar__address flex items-center gap-1.5 pr-4 text-xs tracking-wide hover:underline"
                >
                  <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  <EditableToken
                    path="header.full_address"
                    defaultField="property_full_address"
                    defaultValue="123 Main Street, Atlanta, GA 30313"
                    as="span"
                  >
                    {fullAddress}
                  </EditableToken>
                </a>

                <a
                  href={toTelHref(phone)}
                  type="call"
                  className="header-utility-bar__phone dniphone dniphonehref flex items-center gap-1.5 pl-4 text-xs tracking-wide hover:underline"
                >
                  <PhoneIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  <EditableToken
                    path="header.phone"
                    defaultField="property_call_tracking_phone"
                    defaultValue="(555) 555-5555"
                    as="span"
                  >
                    {formatPhone(phone)}
                  </EditableToken>
                </a>
              </div>

              {/* Right: Utility CTA links (desktop) */}
              <div className="header-utility-bar__ctas hidden md:flex items-center divide-x divide-white/20 ml-auto">
                <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                  <a
                    href={applyNowLink.url || '#'}
                    target={applyNowLink._target || '_blank'}
                    rel="noopener noreferrer"
                    className="px-3 py-1 text-[11px] font-semibold tracking-widest uppercase hover:underline"
                  >
                    {applyNowLink.label || 'Apply Now'}
                  </a>
                </EditableSlotGroup>

                <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                  <a
                    href={residentAccessLink.url || '/resident-access'}
                    target={residentAccessLink._target || '_self'}
                    className="px-3 py-1 text-[11px] font-semibold tracking-widest uppercase hover:underline"
                  >
                    {residentAccessLink.label || 'Resident Access'}
                  </a>
                </EditableSlotGroup>

                <EditableSlotGroup groupPath="header.qualifying_criteria_link" definition={qualifyingCriteriaLinkDef}>
                  <a
                    href={qualifyingCriteriaLink.url || '/qualifying-criteria'}
                    target={qualifyingCriteriaLink._target || '_self'}
                    className="px-3 py-1 text-[11px] font-semibold tracking-widest uppercase hover:underline"
                  >
                    {qualifyingCriteriaLink.label || 'Qualifying Criteria'}
                  </a>
                </EditableSlotGroup>
              </div>

              {/* Mobile utility row: phone + directions */}
              <div className="md:hidden flex items-center justify-between w-full text-xs">
                <a
                  href={toTelHref(phone)}
                  type="call"
                  className="dniphone dniphonehref flex items-center gap-1.5 hover:text-white transition-colors"
                >
                  <PhoneIcon className="w-3.5 h-3.5" />
                  <span>{formatPhone(phone)}</span>
                </a>
                <a
                  href={directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 hover:text-white transition-colors"
                >
                  <MapPinIcon className="w-3.5 h-3.5" />
                  <span>Get Directions</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* ================================================================ */}
        {/* Main Navigation Bar (transparent)                                */}
        {/* ================================================================ */}
        <div className={`main-nav-bar transition-all duration-500 ease-out ${menuOpen ? 'main-nav-bar--menu-open' : scrolled ? 'main-nav-bar--scrolled' : ''}`}>
          <div className="px-4 sm:px-5">
            <div className="flex items-center justify-between h-16 lg:h-[72px]">
              {/* Logo */}
              <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
                <a href="/" className="flex-shrink-0">
                  {logo.image ? (
                    <img
                      src={logo.image}
                      alt={(logo as any).alt || 'Community Logo'}
                      className="h-10 lg:h-12 w-auto object-contain"
                    />
                  ) : (
                    <div className="font-heading text-xl lg:text-2xl font-bold text-white">
                      The Preferred
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              {/* Desktop Navigation Links */}
              <nav className="hidden lg:flex items-center gap-5 xl:gap-7" role="navigation" aria-label="Main navigation">
                {desktopNavItems.map((item, index) => (
                  <NavDropdown
                    key={item.label}
                    item={item}
                    isOpen={openDropdown === index}
                    onOpen={() => setOpenDropdown(index)}
                    onClose={() => setOpenDropdown(null)}
                  />
                ))}
              </nav>

              {/* Desktop CTA Button */}
              <div className="hidden lg:block flex-shrink-0">
                <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                  <a
                    href={scheduleTourLink.url || '/schedule-a-tour'}
                    target={scheduleTourLink._target || '_self'}
                    className="nav-cta-btn group/cta relative inline-flex items-center gap-2 px-7 py-3.5 text-[11px] font-bold tracking-[0.15em] uppercase overflow-hidden transition-all duration-300"
                  >
                    <span className="relative z-10">
                      {scheduleTourLink.label || 'Schedule a Tour'}
                    </span>
                    <svg className="relative z-10 w-3.5 h-3.5 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                    </svg>
                  </a>
                </EditableSlotGroup>
              </div>

              {/* Mobile Hamburger */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="lg:hidden relative w-10 h-10 flex items-center justify-center"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
              >
                <span
                  className={`absolute block w-6 h-0.5 bg-white transition-all duration-300 ${
                    menuOpen ? 'rotate-45' : '-translate-y-2'
                  }`}
                />
                <span
                  className={`absolute block w-6 h-0.5 bg-white transition-all duration-300 ${
                    menuOpen ? 'opacity-0' : ''
                  }`}
                />
                <span
                  className={`absolute block w-6 h-0.5 bg-white transition-all duration-300 ${
                    menuOpen ? '-rotate-45' : 'translate-y-2'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* ================================================================ */}
        {/* Mobile Menu Overlay                                              */}
        {/* ================================================================ */}
        <div
          className={`lg:hidden fixed inset-x-0 bottom-0 mobile-menu-bg overflow-y-auto transition-all duration-300 z-[61] ${
            menuOpen
              ? 'opacity-100 visible'
              : 'opacity-0 invisible pointer-events-none'
          }`}
          style={{ top: `calc(${headerHeight}px + var(--offer-banner-height, 0px))` }}
        >
          {/* Navigation Items */}
          <nav role="navigation" aria-label="Mobile navigation">
            {mobileNavItems.map((item, index) => (
              <MobileNavItem
                key={item.label}
                item={item}
                isOpen={openMobileAccordion === index}
                onToggle={() =>
                  setOpenMobileAccordion(openMobileAccordion === index ? null : index)
                }
                onNavigate={closeMobileMenu}
              />
            ))}
          </nav>

          {/* Schedule a Tour CTA */}
          <div className="p-5">
            <a
              href={scheduleTourLink.url || '/schedule-a-tour'}
              target={scheduleTourLink._target || '_self'}
              onClick={closeMobileMenu}
              className="nav-cta-btn group/cta relative flex items-center justify-center gap-2 w-full py-3.5 text-sm font-bold tracking-[0.15em] uppercase overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{scheduleTourLink.label || 'Schedule a Tour'}</span>
              <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </a>
          </div>

          {/* Utility CTA Buttons */}
          <div className="px-5 pb-3">
            <div className="grid grid-cols-2 gap-2">
              <a
                href={applyNowLink.url || '#'}
                target={applyNowLink._target || '_blank'}
                rel="noopener noreferrer"
                className="text-center py-2.5 text-[11px] font-bold tracking-wider uppercase border border-white/30 text-white/90 hover:bg-white/10 hover:border-white/60 transition-all rounded-sm"
              >
                {applyNowLink.label || 'Apply Now'}
              </a>
              <a
                href={residentAccessLink.url || '/resident-access'}
                target={residentAccessLink._target || '_self'}
                className="text-center py-2.5 text-[11px] font-bold tracking-wider uppercase border border-white/30 text-white/90 hover:bg-white/10 hover:border-white/60 transition-all rounded-sm"
              >
                {residentAccessLink.label || 'Resident Access'}
              </a>
              <a
                href={qualifyingCriteriaLink.url || '/qualifying-criteria'}
                target={qualifyingCriteriaLink._target || '_self'}
                onClick={closeMobileMenu}
                className="text-center py-2.5 text-[11px] font-bold tracking-wider uppercase border border-white/30 text-white/90 hover:bg-white/10 hover:border-white/60 transition-all rounded-sm col-span-2"
              >
                {qualifyingCriteriaLink.label || 'Qualifying Criteria'}
              </a>
            </div>
          </div>

          {/* Address & Phone footer.
              Extra bottom padding (pb-40) reserves clearance for the
              Rentgrata "Ask a Real Resident" floating widget, which is
              injected at runtime and pins itself to the bottom of the
              viewport. Without this the widget's fixed click surface
              overlaps the Residents / Apply Now buttons above and the
              address/phone footer, making them un-tappable on mobile.
              The drawer is `overflow-y-auto`, so this padding simply adds
              scrollable room letting every control clear the widget. */}
          <div className="px-5 pb-40 pt-3 border-t border-white/10">
            <div className="space-y-3">
              <a
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 text-sm text-white/60 hover:text-white transition-colors"
              >
                <MapPinIcon className="w-4 h-4 text-white/80 flex-shrink-0" />
                <span>{fullAddress}</span>
              </a>
              <a
                href={toTelHref(phone)}
                type="call"
                className="dniphone dniphonehref flex items-center gap-2.5 text-sm text-white/60 hover:text-white transition-colors"
              >
                <PhoneIcon className="w-4 h-4 text-white/80 flex-shrink-0" />
                <span>{formatPhone(phone)}</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      <style>{`
        /* ============================================================ */
        /* Utility bar                                                   */
        /* ============================================================ */
        .header-utility-bar {
          background: linear-gradient(
            135deg,
            var(--vibe-color-secondary) 0%,
            color-mix(in srgb, var(--vibe-color-secondary) 85%, black) 100%
          );
          color: var(--vibe-color-text-on-secondary, white);
        }

        /* Tablet only: free space so utility links stay on one line */
        @media (min-width: 768px) and (max-width: 1023.98px) {
          .header-utility-bar__address {
            display: none;
          }
          .header-utility-bar__contact > .header-utility-bar__phone {
            padding-left: 0;
            border-left-width: 0;
            white-space: nowrap;
            flex-shrink: 0;
          }
          .header-utility-bar__ctas > * {
            flex-shrink: 0;
          }
          .header-utility-bar__ctas a {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
            letter-spacing: 0.08em;
            white-space: nowrap;
          }
        }

        /* ============================================================ */
        /* Main nav bar — transparent by default, solid on scroll        */
        /* ============================================================ */
        .main-nav-bar {
          background: transparent;
          border-bottom: 1px solid transparent;
        }
        .main-nav-bar--scrolled {
          background: var(--vibe-color-primary);
          border-bottom-color: transparent;
        }
        .main-nav-bar--menu-open {
          background: color-mix(in srgb, var(--vibe-color-secondary) 95%, black);
          border-bottom-color: rgba(255, 255, 255, 0.1);
        }

        /* ============================================================ */
        /* Mobile menu background                                        */
        /* ============================================================ */
        .mobile-menu-bg {
          background: linear-gradient(
            180deg,
            color-mix(in srgb, var(--vibe-color-secondary) 95%, black) 0%,
            color-mix(in srgb, var(--vibe-color-secondary) 80%, black) 100%
          );
        }

        /* ============================================================ */
        /* Schedule tour CTA button                                      */
        /* ============================================================ */
        .nav-cta-btn {
          color: var(--vibe-color-text-on-primary, white);
          background: linear-gradient(
            135deg,
            var(--vibe-color-primary) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%
          );
          border-radius: 9999px;
          box-shadow:
            0 1px 3px rgba(0,0,0,0.12),
            0 2px 8px color-mix(in srgb, var(--vibe-color-primary) 25%, transparent);
        }
        .nav-cta-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255,255,255,0.15) 50%,
            transparent 100%
          );
          transition: left 0.5s ease;
        }
        .nav-cta-btn:hover {
          background: linear-gradient(
            135deg,
            color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%,
            var(--vibe-color-primary) 100%
          );
          box-shadow:
            0 2px 6px rgba(0,0,0,0.15),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 35%, transparent);
          transform: translateY(-1px);
        }
        .nav-cta-btn:hover::before {
          left: 100%;
        }
        .nav-cta-btn:active {
          transform: translateY(0);
          box-shadow: 0 1px 3px rgba(0,0,0,0.12);
        }
      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'header.full_address': {
      label: 'Full Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.qualifying_criteria_link': {
      label: 'Qualifying Criteria Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/qualifying-criteria' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Qualifying Criteria' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule Tour Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
